import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import LegendItem from "./legendItem";
import { SBContainer } from "pages/provider/claimCodingOpt/styles";
import { getClaimProgressNoteDocument } from "apiHandler/actions";
import DocumentViewer from "./documentViewer";
import ScoreComparison from "./scoreComparison";
import { trimmedDate } from "utils/helper";

const ClaimPatientInfo = ({
  claim,
  showICD,
  showCPT,
  token,
  practiceID,
  setSnackbarSeverity,
  setSnackbarMessage,
  setSnackbarOpen,
  showOriginal = true,
}) => {
  const [documentUrl, setDocumentUrl] = useState(null);
  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const getDocument = async () => {
    setLoading(true);
    setSnackbarSeverity("info");
    setSnackbarMessage("Getting Document...");
    setSnackbarOpen(true);
    try {
      const payload = {
        practice_id: practiceID,
        claim_no: claim?.claim_no,
      };

      const blob = await getClaimProgressNoteDocument(payload, token);
      const mimeType = blob.type || "application/octet-stream";
      const url = URL.createObjectURL(blob);

      setDocumentUrl({ url, mimeType });
      setOpenDocDialog(true);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDocDialog = () => {
    setOpenDocDialog(false);
    if (documentUrl?.url && documentUrl?.url.startsWith("blob:")) {
      URL.revokeObjectURL(documentUrl.url);
    }
    setDocumentUrl(null);
  };

  return (
    <Box mt={2} mb={3}>
      <SBContainer>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          Encounter / Patient Information
        </Typography>
        <Button variant="contained" disabled={loading} onClick={getDocument}>
          Progress Note
        </Button>
      </SBContainer>
      <Box display="flex" gap={2} mb={1}>
        <LegendItem label="Encounter No" value={claim?.claim_no} />
        <LegendItem
          label="Service Date"
          value={trimmedDate(claim?.service_date)}
        />
        <LegendItem label="Visit Type" value={claim?.visit_type} />
        <LegendItem label="Is Televisit" value={claim?.is_televisit} />
        <LegendItem
          label="Rendering Provider"
          value={claim?.rendering_provider}
        />
      </Box>
      <Box display="flex" gap={2} mb={1}>
        <LegendItem label="Patient Account No" value={claim?.patient_acct_no} />
        <LegendItem label="Patient Name" value={claim?.patient_name} />
        <LegendItem label="Gender" value={claim?.patient_gender} />
        <LegendItem label="Age" value={claim?.patient_age} />
      </Box>
      <Box
        display="flex"
        gap={2}
        p={1}
        alignItems="center"
        sx={{ backgroundColor: "#f5f7fa", borderRadius: 1 }}
        justifyContent={"space-evenly"}
        mb={1}
      >
        {showICD && (
          <ScoreComparison
            originalScore={claim?.original_raf_score}
            aiScore={claim?.ai_recommended_raf_score}
            showOriginal={showOriginal}
            isIcd={true}
          />
        )}
        {showCPT && (
          <ScoreComparison
            originalScore={claim?.original_rvu_total}
            aiScore={claim?.ai_recommended_rvu_total}
            maxValue={15}
            showOriginal={showOriginal}
          />
        )}
        <Typography>
          {claim?.overall_summary &&
            claim?.overall_summary.split("\n").map((line, idx) => (
              <span key={idx}>
                {line}
                <br />
              </span>
            ))}
        </Typography>
      </Box>

      <DocumentViewer
        openDocDialog={openDocDialog}
        documentUrl={documentUrl}
        handleCloseDocDialog={handleCloseDocDialog}
      />
    </Box>
  );
};

export default ClaimPatientInfo;
